import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {
  ContentUploadOptions,
  CreateDigitalProductInput,
  CustomerPurchase,
  DateRange,
  DigitalProductSummary,
  PaginatedResponse,
  ProductContent,
  SalesInsights,
  UpdateDigitalProductInput,
  AddOn,
  SaveAddOnsInput,
} from "../domain/digital-product.types";
import {DigitalProduct} from "../domain/digital-product.entity";
import {createDigitalProductRepository} from "../data/repositories";
import {digitalProductsQueryKeys} from "./query-keys";

/**
 * Hook for fetching paginated list of digital products.
 *
 * Uses the repository factory, which always returns the v2 implementation.
 *
 * @param page - Page number (1-indexed)
 * @param perPage - Number of items per page
 * @returns React Query result with paginated DigitalProductSummary
 */
export const useDigitalProducts = (page: number = 1, perPage: number = 5) => {
  const repository = createDigitalProductRepository();

  return useQuery<
    PaginatedResponse<DigitalProductSummary>,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: digitalProductsQueryKeys.list({page, perPage}),
    queryFn: () => repository.getList({page, perPage}),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};

/**
 * Hook for fetching a single digital product.
 *
 * Uses the repository factory, which always returns the v2 implementation.
 *
 * @param productId - The ID of the product to fetch (optional, query disabled if not provided)
 * @returns React Query result with DigitalProduct entity
 */
export const useDigitalProduct = (productId?: number) => {
  const repository = createDigitalProductRepository();

  return useQuery<DigitalProduct, AxiosError<{status: boolean; message: string}>>({
    queryKey: digitalProductsQueryKeys.detail(productId ?? 0),
    queryFn: () => repository.getSingle(productId!),
    enabled: !!productId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for digital product mutations (create, update).
 *
 * Provides mutations for creating and updating products with automatic cache
 * invalidation. Delete mutation will be added in a later phase.
 *
 * @returns Object with create and update mutations
 */
export const useDigitalProductMutations = () => {
  const repository = createDigitalProductRepository();
  const queryClient = useQueryClient();

  const createMutation = useMutation<
    DigitalProduct,
    AxiosError<{status: boolean; message: string}>,
    CreateDigitalProductInput
  >({
    mutationFn: (input) => repository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: digitalProductsQueryKeys.lists()});
    },
  });

  const updateMutation = useMutation<
    DigitalProduct,
    AxiosError<{status: boolean; message: string}>,
    UpdateDigitalProductInput
  >({
    mutationFn: (input) => repository.update(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: digitalProductsQueryKeys.lists()});
      queryClient.invalidateQueries({
        queryKey: digitalProductsQueryKeys.detail(data.id),
      });
    },
  });

  const deleteMutation = useMutation<
    void,
    AxiosError<{status: boolean; message: string}>,
    number
  >({
    mutationFn: (productId) => repository.remove(productId),
    onSuccess: (_data, productId) => {
      queryClient.invalidateQueries({queryKey: digitalProductsQueryKeys.lists()});
      queryClient.removeQueries({queryKey: digitalProductsQueryKeys.detail(productId)});
    },
  });

  return {
    createProduct: createMutation.mutate,
    createProductAsync: createMutation.mutateAsync,
    isCreating: createMutation.isLoading,
    createError: createMutation.error,
    createSuccess: createMutation.isSuccess,
    createdProduct: createMutation.data,
    updateProduct: updateMutation.mutate,
    updateProductAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isLoading,
    updateError: updateMutation.error,
    updateSuccess: updateMutation.isSuccess,
    deleteProduct: deleteMutation.mutate,
    deleteProductAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isLoading,
    deleteError: deleteMutation.error,
    deleteSuccess: deleteMutation.isSuccess,
  };
};

/**
 * Hook for fetching a digital product's downloadable content files.
 *
 * A product can hold multiple files, so this returns an array (empty if none).
 *
 * @param productId - Product whose content to fetch (query disabled if absent)
 * @returns React Query result with a list of ProductContent
 */
export const useDigitalProductContentList = (productId?: number) => {
  const repository = createDigitalProductRepository();

  return useQuery<ProductContent[], AxiosError<{status: boolean; message: string}>>({
    queryKey: digitalProductsQueryKeys.content(productId ?? 0),
    queryFn: () => repository.getContentList(productId!),
    enabled: !!productId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for digital product content mutations (upload, delete).
 *
 * Invalidates the content query on success so the tab reflects the change.
 *
 * @param productId - Product whose content is being mutated
 */
export const useDigitalProductContentMutations = (productId: number) => {
  const repository = createDigitalProductRepository();
  const queryClient = useQueryClient();

  const invalidateContent = () =>
    queryClient.invalidateQueries({
      queryKey: digitalProductsQueryKeys.content(productId),
    });

  const uploadMutation = useMutation<
    void,
    AxiosError<{status: boolean; message: string}>,
    {file: File; options?: ContentUploadOptions}
  >({
    mutationFn: ({file, options}) => repository.uploadContent(productId, file, options),
    onSuccess: invalidateContent,
  });

  const deleteMutation = useMutation<
    void,
    AxiosError<{status: boolean; message: string}>,
    {contentId: number}
  >({
    mutationFn: ({contentId}) => repository.deleteContent(productId, contentId),
    onSuccess: invalidateContent,
  });

  return {
    uploadContent: uploadMutation.mutate,
    uploadContentAsync: uploadMutation.mutateAsync,
    isUploadingContent: uploadMutation.isLoading,
    uploadContentError: uploadMutation.error,
    deleteContent: deleteMutation.mutate,
    deleteContentAsync: deleteMutation.mutateAsync,
    isDeletingContent: deleteMutation.isLoading,
    deleteContentError: deleteMutation.error,
  };
};

/**
 * Hook for fetching a digital product's add-ons (variants).
 *
 * Returns an array (empty when the product has none). v2 exposes a dedicated
 * add-ons endpoint; v1 reads them from the product payload — both behind the
 * repository factory.
 *
 * @param productId - Product whose add-ons to fetch (query disabled if absent)
 */
export const useDigitalProductAddOns = (productId?: number) => {
  const repository = createDigitalProductRepository();

  return useQuery<AddOn[], AxiosError<{status: boolean; message: string}>>({
    queryKey: digitalProductsQueryKeys.addOns(productId ?? 0),
    queryFn: () => repository.getAddOns(productId!),
    enabled: !!productId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for a digital product's add-on mutations (create, update, delete group,
 * delete option).
 *
 * Invalidates the add-ons query and the product detail (which carries
 * `hideVariantFromProfile`) on success.
 *
 * @param productId - Product whose add-ons are being mutated
 */
export const useDigitalProductAddOnMutations = (productId: number) => {
  const repository = createDigitalProductRepository();
  const queryClient = useQueryClient();

  const invalidateAddOns = () => {
    queryClient.invalidateQueries({queryKey: digitalProductsQueryKeys.addOns(productId)});
    queryClient.invalidateQueries({
      queryKey: digitalProductsQueryKeys.detail(productId),
    });
  };

  const createMutation = useMutation<
    AddOn[],
    AxiosError<{status: boolean; message: string}>,
    SaveAddOnsInput
  >({
    mutationFn: (input) => repository.createAddOns(productId, input),
    onSuccess: invalidateAddOns,
  });

  const updateMutation = useMutation<
    AddOn[],
    AxiosError<{status: boolean; message: string}>,
    SaveAddOnsInput
  >({
    mutationFn: (input) => repository.updateAddOns(productId, input),
    onSuccess: invalidateAddOns,
  });

  const deleteAddOnMutation = useMutation<
    void,
    AxiosError<{status: boolean; message: string}>,
    {addOnId: string}
  >({
    mutationFn: ({addOnId}) => repository.deleteAddOn(productId, addOnId),
    onSuccess: invalidateAddOns,
  });

  const deleteOptionMutation = useMutation<
    void,
    AxiosError<{status: boolean; message: string}>,
    {addOnId: string; optionId: string}
  >({
    mutationFn: ({addOnId, optionId}) =>
      repository.deleteAddOnOption(productId, addOnId, optionId),
    onSuccess: invalidateAddOns,
  });

  return {
    createAddOns: createMutation.mutate,
    createAddOnsAsync: createMutation.mutateAsync,
    isCreatingAddOns: createMutation.isLoading,
    createAddOnsError: createMutation.error,
    createdAddOns: createMutation.data,
    updateAddOns: updateMutation.mutate,
    updateAddOnsAsync: updateMutation.mutateAsync,
    isUpdatingAddOns: updateMutation.isLoading,
    updateAddOnsError: updateMutation.error,
    updatedAddOns: updateMutation.data,
    deleteAddOn: deleteAddOnMutation.mutate,
    deleteAddOnAsync: deleteAddOnMutation.mutateAsync,
    isDeletingAddOn: deleteAddOnMutation.isLoading,
    deleteAddOnError: deleteAddOnMutation.error,
    deleteAddOnOption: deleteOptionMutation.mutate,
    deleteAddOnOptionAsync: deleteOptionMutation.mutateAsync,
    isDeletingAddOnOption: deleteOptionMutation.isLoading,
    deleteAddOnOptionError: deleteOptionMutation.error,
  };
};

/**
 * Builds an all-time analytics range: from the product's creation date (or a
 * fixed early date) to today. Avoids a date-picker UI per the migration plan.
 */
const getAllTimeRange = (createdAt?: string): DateRange => ({
  startDate: createdAt ? createdAt.slice(0, 10) : "2020-01-01",
  endDate: new Date().toISOString().slice(0, 10),
});

/**
 * Hook for a digital product's sales insights (all-time).
 *
 * @param productId - Product to fetch insights for (query disabled if absent)
 * @param createdAt - Product creation date, used as the range start
 */
export const useDigitalProductSalesInsights = (
  productId?: number,
  createdAt?: string
) => {
  const repository = createDigitalProductRepository();

  return useQuery<SalesInsights, AxiosError<{status: boolean; message: string}>>({
    queryKey: digitalProductsQueryKeys.insights(productId ?? 0),
    queryFn: () => repository.getSalesInsights(productId!, getAllTimeRange(createdAt)),
    enabled: !!productId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for a digital product's customer purchases (paginated, all-time).
 *
 * @param productId - Product to fetch purchases for (query disabled if absent)
 * @param page - 1-indexed page
 * @param perPage - Page size
 * @param createdAt - Product creation date, used as the range start
 */
export const useDigitalProductCustomerPurchases = (
  productId?: number,
  page: number = 1,
  perPage: number = 5,
  createdAt?: string
) => {
  const repository = createDigitalProductRepository();

  return useQuery<
    PaginatedResponse<CustomerPurchase>,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: digitalProductsQueryKeys.purchases(productId ?? 0, page),
    queryFn: () =>
      repository.getCustomerPurchases(
        productId!,
        {page, perPage},
        getAllTimeRange(createdAt)
      ),
    enabled: !!productId,
    keepPreviousData: true,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to export a digital product's customer purchases as a CSV blob (all-time).
 *
 * @param productId - Product to export purchases for
 * @param createdAt - Product creation date, used as the range start
 */
export const useDigitalProductExportPurchases = (
  productId: number,
  createdAt?: string
) => {
  const repository = createDigitalProductRepository();

  return useMutation<Blob, AxiosError<{status: boolean; message: string}>, void>({
    mutationFn: () =>
      repository.exportCustomerPurchases(productId, getAllTimeRange(createdAt)),
  });
};

export * from "./query-keys";
